import { useCallback,useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchRoleUserPaginatedMemberships, fetchRoleUserPaginatedMembershipsForExport } from 'common/services/api/RoleUserMembership/RoleUserMembership.thunk';
import { ActionDefinition, Operations } from 'common/types/TypeDefs';
import { userMembershipColumnsDefinitionWithActions } from 'common/utils/userMembershipColumnsHelper';

import { useRoleDropDownFiltersConfiguration } from './helpers';
import { RoleUserMembershipCreateDrawerContent } from './RoleUserMembershipCreateDrawerContent';
import { RoleUserMembershipDeleteDialogContent } from './RoleUserMembershipDeleteDialogContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilteredStatus,
    selectFilters
} from './roleUserMembershipSlice';

export const RoleUserMembershipTab = () => {
    const selectedFilters = useAppSelector(selectFilters);
    const filteredStatus = useAppSelector(selectFilteredStatus);

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.MultiTenantRO, Roles.AppManager ]);

    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const filters = useAppSelector(selectFilters);

    const params = useParams();
    const { suiteId, tenantId = '', roleId = '' } = params;

    const dispatch = useAppDispatch();
    const [processing, setProcessing] = useState<boolean>(false);
    const [defaultSortModel] = usePalmSortModel('principalName');

    const fetchGridData = usePalmFetchPaginatedEntities(
        'User',
        { tenantId, roleId, status: filteredStatus, skip: 0, take: 0 },
        fetchRoleUserPaginatedMemberships,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        useRoleDropDownFiltersConfiguration()
    );

    const onGetRowLink = useCallback((userId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}/${userId}/${Routes.Roles}`;
    }, []);

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchRoleUserPaginatedMembershipsForExport({
            tenantId: tenantId ?? '',
            roleId: roleId ?? '',
            search: search ?? '',
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal: signal,
            status: selectedFilters.dropdowns.status.keys
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const actionDefinitions = [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions = userMembershipColumnsDefinitionWithActions;

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        title='User'
        isCreateButtonDisabled={shouldDisableButtons}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        filter={filterProp}
        processing={processing}
        operationName={Operations.Link}
        contentFunction={RoleUserMembershipCreateDrawerContent}
        deleteFunction={RoleUserMembershipDeleteDialogContent}
        customDeleteTitle={'Are you sure you want to remove this User from this Role?'}
        paginationMode={paginationMode}
        searchFilterDisabled={filters.keywords.length !== 0}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT,
        }}
        refreshDataGridClick={refreshDataGrid}
        sortModel={paginationGridData?.sortModel ?? defaultSortModel}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
    />;
};
